import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mic, MicOff } from 'lucide-react';
import { continuousOk, freshPart, speakPunct, joinSpeech } from '../lib/speech';
import { speechLang } from '../lib/photoAi';

export default function DictateField({ t, as = 'input', value, onChange, testid, placeholder, className = '' }) {
  const elRef = useRef(null);
  const srRef = useRef(null);
  const st = useRef({ val: value, pos: 0, mem: { last: '', at: 0 } });
  const [rec, setRec] = useState(false);
  const [interim, setInterim] = useState('');
  st.current.val = value;
  const cb = useRef(onChange);
  cb.current = onChange;

  useEffect(() => () => { if (srRef.current) { srRef.current.active = false; srRef.current.stop(); } }, []);

  const insert = (txt) => {
    const { val, pos } = st.current;
    const before = val.slice(0, pos), after = val.slice(pos);
    const head = joinSpeech(before, txt);
    const chunk = head.slice(before.length) + (after && !/^\s/.test(after) && !/\n$/.test(txt) ? ' ' : '');
    const next = before + chunk + after;
    st.current.val = next;
    st.current.pos = pos + chunk.length;
    cb.current(next);
    requestAnimationFrame(() => { const el = elRef.current; if (el) { el.focus(); el.setSelectionRange(st.current.pos, st.current.pos); } });
  };

  const stop = () => {
    const sr = srRef.current;
    if (sr) { sr.active = false; try { sr.stop(); } catch { /* ignore */ } }
    srRef.current = null;
    setRec(false);
    setInterim('');
  };

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error(t.dcNoSR, { duration: 8000 });
    const el = elRef.current;
    st.current.pos = el && el.selectionStart != null && document.activeElement === el ? el.selectionStart : (value || '').length;
    const sr = new SR();
    sr.lang = speechLang(t);
    sr.continuous = continuousOk;
    sr.interimResults = true;
    sr.active = true;
    sr.seen = new Set();
    sr.onstart = () => { sr.seen = new Set(); };
    sr.onresult = (e) => {
      let im = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          if (sr.seen.has(i)) continue;
          sr.seen.add(i);
          const part = freshPart(st.current.mem, e.results[i][0].transcript);
          if (part) insert(speakPunct(part, t.locale));
        }
        else im += e.results[i][0].transcript;
      }
      setInterim(im);
    };
    sr.onerror = (e) => {
      const msg = { 'not-allowed': t.dcBlocked, 'service-not-allowed': t.dcBlocked, network: t.dcNet, 'audio-capture': t.dcNoMic }[e.error];
      if (msg) { toast.error(msg, { duration: 10000 }); stop(); }
    };
    sr.onend = () => { if (sr.active) { try { sr.start(); } catch { stop(); } } };
    srRef.current = sr;
    try { sr.start(); setRec(true); } catch { toast.error(t.dcBlocked, { duration: 10000 }); }
  };

  const Tag = as;
  return (
    <div className={`dictate-field ${as === 'textarea' ? 'is-area' : ''} ${rec ? 'is-rec' : ''} ${className}`}>
      <Tag ref={elRef} type={as === 'input' ? 'text' : undefined} data-testid={testid} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      <button type="button" className={`dictate-btn ${rec ? 'rec' : ''}`} data-testid={`${testid}-dictate-btn`} onClick={rec ? stop : start} title={rec ? t.dictating : t.dictate} aria-label={t.dictate}>
        {rec ? <MicOff size={15} /> : <Mic size={15} />}
      </button>
      {rec && <div className="dictate-status" data-testid={`${testid}-dictate-status`}>{t.dictating}{interim && <em> „{interim}“</em>}</div>}
    </div>
  );
}
